import { computed, ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { createQueryKey } from '@/utils'
import { mapPresentation, mapResourceGuide } from '@/utils/cmsContentMappings'
import { useCmsContentSearch } from '@/composables/useCmsContentSearch'
import IbexaContentTypeIdentifier from '@/enums/IbexaContentTypeIdentifier'

export const usePresentation = (contentId: number) => {
  const { search } = useCmsContentSearch()

  const currentSlideIndex = ref<number>(0)
  const resourceGuideContentId = ref<number | null>(null)

  const searchQuery = {
    ['contentTypes[]']: IbexaContentTypeIdentifier.vgsPresentation,
    ['contentId[]']: contentId.toString(),
  }

  const createResourceGuideQuery = () => ({
    ['contentTypes[]']: IbexaContentTypeIdentifier.vgsResourceGuide,
    ['contentId[]']: resourceGuideContentId.value?.toString(),
  })

  const { data } = useQuery({
    queryKey: createQueryKey('presentation', contentId),
    queryFn: () => search(searchQuery),
    staleTime: 1000 * 60 * 60 * 8,
  })

  const { data: resourceGuideData } = useQuery({
    queryKey: createQueryKey('resourceGuide', resourceGuideContentId.value as number),
    queryFn: () => search(createResourceGuideQuery()),
    enabled: computed(() => !!resourceGuideContentId.value),
    staleTime: 1000 * 60 * 60 * 8,
  })

  const goToNextSlide = () => currentSlideIndex.value < (slides.value?.length ?? 0) - 1 ? currentSlideIndex.value++ : slides.value?.length
  const goToPrevSlide = () => currentSlideIndex.value > 0 ? currentSlideIndex.value-- : 0
  const goToSlide = (targetSlide: number) => currentSlideIndex.value = targetSlide

  const mappedData = computed(() => {
    if (!data.value?.[0]) return null
    const mappedData = mapPresentation(data.value[0])
    resourceGuideContentId.value = mappedData?.resourceGuide
    return mappedData
  })

  const slides = computed(() => mappedData.value?.slides)
  const colorPair = computed(() => mappedData.value?.colorPair)
  const resourceGuide = computed(() => {
    if (!resourceGuideData.value?.length) return null

    return mapResourceGuide(resourceGuideData.value[0])
  })

  return {
    colorPair,
    currentSlideIndex,
    resourceGuide,
    slides,
    goToNextSlide,
    goToPrevSlide,
    goToSlide,
  }
}

export default usePresentation
